import { useContext, useEffect, useState, useSyncExternalStore } from 'react';
import { SILENCE_STALE_MS, isStale } from '../lib/staleness';
import type { DisplayState, Domain } from '../protocol/types';
import { DisplayContext, type DisplayCtx } from './DisplayContext';
import type { DisplaySocket } from './DisplaySocket';
import type { StoreState } from './store';

function useCtx(): DisplayCtx {
  const c = useContext(DisplayContext);
  if (!c) throw new Error('Gli hook del display vanno usati dentro <DisplayProvider>');
  return c;
}

/** Selettore generico sullo store (stesso riferimento se la slice non cambia). */
export function useDisplaySelector<T>(selector: (s: StoreState) => T): T {
  const { store } = useCtx();
  return useSyncExternalStore(store.subscribe, () => selector(store.getState()));
}

/** Una slice di dominio, `undefined` finché non arriva lo snapshot. */
export function useDomain<D extends Domain>(domain: D): DisplayState[D] | undefined {
  return useDisplaySelector((s) => s.data[domain] as DisplayState[D] | undefined);
}

export function useConnection() {
  return useDisplaySelector((s) => s.connection);
}

export function useSocket(): DisplaySocket {
  return useCtx().socket;
}

/** Ora "di server", aggiornata ogni secondo e corretta con lo scarto rilevato su welcome/pong. */
export function useServerClock(): Date {
  const offset = useDisplaySelector((s) => s.clockOffsetMs);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);
  return new Date(now + offset);
}

/** true se la slice è più vecchia di maxAgeMs o assente: il pannello mostra "dato non aggiornato". */
export function useIsStale(updatedAt: number | undefined, maxAgeMs: number): boolean {
  const now = useServerClock().getTime();
  return updatedAt === undefined || now - updatedAt > maxAgeMs;
}

/** true se la slice del dominio è assente o più vecchia della soglia del dominio (vedi lib/staleness). */
export function useDomainStale(domain: Domain): boolean {
  const slice = useDisplaySelector((s) => s.data[domain]) as
    { updatedAt: number; measuredAt?: number } | undefined;
  const lastMessageAt = useDisplaySelector((s) => s.connection.lastMessageAt);
  const offset = useDisplaySelector((s) => s.clockOffsetMs);
  const now = useServerClock().getTime();
  // Connessione muta da troppo: mood, caffè e gli altri domini a evento non possono dirsi "di oggi".
  // lastMessageAt è in ora locale, `now` in ora di server: si toglie lo scarto per confrontarli.
  const silent = lastMessageAt !== null && now - offset - lastMessageAt > SILENCE_STALE_MS;
  return silent || isStale(domain, slice, now);
}

/** Secondi trascorsi da un istante (ora di server), mai negativi; null se l'istante manca. */
export function useSecondsAgo(ts: number | undefined): number | null {
  const now = useServerClock().getTime();
  return ts === undefined ? null : Math.max(0, Math.floor((now - ts) / 1000));
}
